import { IGroup } from "@fluentui/react";

/**
 * The locations grid's grouping. The grid hands Fluent a flat, ordered items array plus index
 * ranges per group, so the order in which the sections are concatenated IS the group order.
 * The load and the column sort both go through here, from one ordered section list, so a
 * new section (or a moved one) cannot land in one path and not the other.
 */

export interface ILocationSection<T> {
    key: string;
    name: string;
    items: T[] | undefined;
}

export interface ILocationSectionInput<T> {
    hubAssociatedHubs?: T[];
    hubAssociatedSites?: T[];
    subSites?: T[];
    channels?: T[];
    tessProjectFolders?: T[];
    documentLibraries?: T[];
    /** Client-group members of the current site (accountancy tenants). */
    clientGroupSites?: T[];
    /** One section per client group the current site belongs to: `key` is the group's item id,
     * `name` the group's title, the rows the group's site followed by the other members. */
    clientGroups?: ILocationSection<T>[];
    tessActiveProjects?: T[];
    tessDraftProjects?: T[];
    tessFinishedProjects?: T[];
    sharepointLists?: T[];
}

/** The language strings the group headers use; the same keys as LocalizationModule. */
export interface ILocationSectionLabels {
    HubAssociatedHubs: string;
    HubAssociatedSites: string;
    Subsites: string;
    Channels: string;
    TessFolders: string;
    DocumentLibraries: string;
    ClientGroup: string;
    TessActiveProjects: string;
    TessConceptProjects: string;
    TessCompletedProjects: string;
    Lists: string;
}

/** The fixed section order. The client-group sections follow the libraries, so a site's own
 * content stays above its members and above the groups it belongs to, the way the site trees
 * list them: own members first, then one section per group in the order given. */
export const orderLocationSections = <T>(input: ILocationSectionInput<T>, labels: ILocationSectionLabels): ILocationSection<T>[] => [
    { key: "hubAssociatedHubsGroup", name: labels.HubAssociatedHubs, items: input.hubAssociatedHubs },
    { key: "hubAssociatedSitesGroup", name: labels.HubAssociatedSites, items: input.hubAssociatedSites },
    { key: "subSitesGroup", name: labels.Subsites, items: input.subSites },
    { key: "channelsGroup", name: labels.Channels, items: input.channels },
    { key: "tessProjectFolders", name: labels.TessFolders, items: input.tessProjectFolders },
    { key: "documentLibrariesGroup", name: labels.DocumentLibraries, items: input.documentLibraries },
    { key: "clientGroupSitesGroup", name: labels.ClientGroup, items: input.clientGroupSites },
    ...(input.clientGroups ?? []).map(group => ({ key: `clientGroupsGroup:${group.key}`, name: group.name, items: group.items })),
    { key: "activeProjects", name: labels.TessActiveProjects, items: input.tessActiveProjects },
    { key: "draftProjects", name: labels.TessConceptProjects, items: input.tessDraftProjects },
    { key: "finishedProjects", name: labels.TessCompletedProjects, items: input.tessFinishedProjects },
    { key: "sharepointListsGroup", name: labels.Lists, items: input.sharepointLists }
];

/** Empty sections yield no group; every other section becomes one contiguous index range. */
export const composeLocationSections = <T>(sections: ILocationSection<T>[]): { groups: IGroup[]; items: T[] } => {
    const groups: IGroup[] = [];
    const items: T[] = [];
    sections.forEach(section => {
        const rows: T[] = Array.isArray(section.items) ? section.items : [];
        if (rows.length === 0) {
            return;
        }
        groups.push({ key: section.key, name: section.name, startIndex: items.length, count: rows.length, level: 0 });
        rows.forEach(row => items.push(row));
    });
    return { groups, items };
};

/** Freshly composed groups that keep the collapsed state the user gave the groups already on
 * screen, by key. For sections that join a grid after it rendered (the client group lookup). */
export const carryCollapsedGroups = (groups: IGroup[], previous: IGroup[] | null | undefined): IGroup[] => {
    if (!Array.isArray(previous) || previous.length === 0) {
        return groups;
    }
    const collapsed: Map<string, boolean | undefined> = new Map<string, boolean | undefined>(previous.map(group => [group.key, group.isCollapsed]));
    return groups.map(group => collapsed.has(group.key) ? { ...group, isCollapsed: collapsed.get(group.key) } : group);
};
